import CssBaseline from '@mui/material/CssBaseline';
import { createRoot } from 'react-dom/client';
import { createInertiaApp } from '@inertiajs/react';
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers';
import { ThemeProvider } from '@mui/material/styles';
import React from 'react';
import * as Sentry from '@sentry/react';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV2';
import { LicenseInfo } from '@mui/x-license';

import theme from './theme';

import { layoutFor } from '@/core/pageLayout';
import {
  recoverFromStaleAssets,
  registerStaleAssetRecovery,
} from '@/core/selfHealingReload';

registerStaleAssetRecovery();

LicenseInfo.setLicenseKey(import.meta.env.VITE_MUI_X_LICENSE_KEY || '');

const setUpSentry = (): void => {
  if (import.meta.env.MODE === 'development') {
    return;
  }

  Sentry.init({
    dsn: 'https://8ea587eea37b4f3f9c05c846a82c91ca@o155756.ingest.sentry.io/4504532807450624',
    integrations: [
      new Sentry.BrowserTracing({
        tracePropagationTargets: ['localhost', /^\//],
      }),
      new Sentry.Replay({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 0.01,
    replaysOnErrorSampleRate: 1.0,
    environment: import.meta.env.MODE,
  });
};

void createInertiaApp({
  title: (title) => `${title} - Harness`,
  resolve: async (name) => {
    let module: any;
    try {
      module = await resolvePageComponent<any>(
        `./pages/${name}.tsx`,
        import.meta.glob([
          './pages/**/*.tsx',
          '!./pages/**/components/**',
          '!./pages/shared/**',
          '!./pages/activity/**',
        ]),
      );
    } catch (error) {
      // The page chunk is missing because a deploy replaced it. Surface the
      // updating banner and reload rather than letting the navigation hang
      // on a broken import. Re-throw so Inertia does not proceed with a
      // half-resolved page before the reload kicks in.
      recoverFromStaleAssets();
      throw error;
    }
    const page = module.default;
    const layout = layoutFor(name);

    if (layout) {
      page.layout = layout;
    }

    return page;
  },
  setup({ el, App, props }) {
    const root = createRoot(el);

    setUpSentry();

    root.render(
      <React.StrictMode>
        <ThemeProvider theme={theme}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <CssBaseline />
            <App {...props} />
          </LocalizationProvider>
        </ThemeProvider>
      </React.StrictMode>,
    );
  },
  progress: {
    color: '#4B5563',
  },
});
